import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { formatLocalPeriod } from "@/lib/utils";

const adminAll = ["admin"] as const;

const adminUsersKeys = (params?: Record<string, unknown>) => [...adminAll, "users", params] as const;
const adminUserKey = (id: number) => [...adminAll, "users", id] as const;
const adminSettingsKey = () => [...adminAll, "settings"] as const;
const adminBackupsKeys = (params?: Record<string, unknown>) => [...adminAll, "backups", params] as const;
const billingAll = [...adminAll, "billing"] as const;
/**
 * The all-pages periods walk key (devil-gate D6). Exported so a mutation living
 * in ANOTHER hook can invalidate it: `useRegisterPayment` (hooks/useContracts)
 * invalidated only its own `["admin","contracts"]` family, which never reaches
 * `["admin","billing","periods","all-pages"]` — the drawer's history table kept
 * the pre-payment rows. Keep the literal in ONE place; the drawer's manual
 * `refetchAllPeriods()` stays as belt-and-braces.
 */
export const billingPeriodsAllPagesKey = () => [...billingAll, "periods", "all-pages"] as const;
const billingSummaryKey = () => [...billingAll, "summary"] as const;
const billingEnginesKeys = (params?: Record<string, unknown>) => [...billingAll, "engines", params] as const;
const billingOverageKey = () => [...billingAll, "overage"] as const;
const billingAntiFraudKey = () => [...billingAll, "anti-fraud"] as const;

export function useAdminUsers(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: adminUsersKeys(params),
    queryFn: () => apiClient.get<{ data: unknown[]; total: number; page: number; limit: number }>("/api/admin/users", params as Record<string, string>),
  });
}

export function useAdminUser(userId: number) {
  return useQuery({
    queryKey: adminUserKey(userId),
    queryFn: () => apiClient.get<Record<string, unknown>>(`/api/admin/users/${userId}`),
    enabled: userId > 0,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.post<Record<string, unknown>>("/api/admin/users", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys() }),
  });
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...body }: { userId: number } & Record<string, unknown>) =>
      apiClient.patch<Record<string, unknown>>(`/api/admin/users/${userId}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys() }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => apiClient.delete(`/api/admin/users/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys() }),
  });
}

export function useSuspendUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => apiClient.post<Record<string, unknown>>(`/api/admin/users/${userId}/suspend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys() }),
  });
}

export function useActivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: number) => apiClient.post<Record<string, unknown>>(`/api/admin/users/${userId}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys() }),
  });
}

export function stripAdminPrefix(settings: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(settings)) {
    const strippedKey = key.startsWith("admin.") ? key.slice(6) : key;
    result[strippedKey] = value;
  }
  return result;
}

export function useAdminSettings() {
  return useQuery({
    queryKey: adminSettingsKey(),
    queryFn: () => apiClient.get<Record<string, unknown>>("/api/admin/settings"),
    select: (data) => stripAdminPrefix(data),
  });
}

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.put<{ success: boolean }>("/api/admin/settings", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminSettingsKey() }),
  });
}

export interface BackupRetentionFormValues {
  retention_max_count: number;
  retention_max_age_days: number;
}

// The GET flat map always carries the EFFECTIVE scalars (spec AC-1: routes/
// admin/settings.ts injects defaults 30/90 when the `backup.*` rows are
// absent, and stripAdminPrefix leaves the dotted `backup.` keys untouched).
// A null here means "still loading" or "no access" — consumers treat the
// value as optional (the Backups page badge is optional by design, AC-6).
function toRetentionNumber(raw: unknown): number | null {
  return typeof raw === "number" && Number.isInteger(raw) && raw >= 0 ? raw : null;
}

export function useBackupRetention() {
  const { data, isLoading } = useAdminSettings();
  return {
    retentionMaxCount: toRetentionNumber(data?.["backup.retention_max_count"]),
    retentionMaxAgeDays: toRetentionNumber(data?.["backup.retention_max_age_days"]),
    isLoading,
  };
}

export function useSaveBackupRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BackupRetentionFormValues) =>
      apiClient.patch<{ success: boolean; settings: Record<string, number> }>(
        "/api/admin/settings/backup-retention",
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminSettingsKey() }),
  });
}

export function useTestMail() {
  return useMutation({
    mutationFn: (recipient: string) =>
      apiClient.post<{ success: boolean; message: string }>("/api/admin/settings/test-mail", { recipient }),
  });
}

export function useTestRCControlToken() {
  return useMutation({
    mutationFn: (token: string) =>
      apiClient.post<{ success: boolean; message: string }>("/api/admin/integrations/test-token", { token }),
  });
}

export function useAdminBackups(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: adminBackupsKeys(params),
    queryFn: () => apiClient.get<{ data: unknown[]; total: number; page: number; limit: number }>("/api/admin/backups", params as Record<string, string>),
  });
}

export function useTriggerBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post<{ backup_id: string; status: string; message: string }>("/api/admin/backups/trigger"),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminBackupsKeys() }),
  });
}

export function useDeleteBackup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (backupId: number) => apiClient.delete(`/api/admin/backups/${backupId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminBackupsKeys() }),
  });
}

export function useCompareBackups() {
  return useMutation({
    mutationFn: (ids: { backup_id_1: number; backup_id_2: number }) =>
      apiClient.post<Record<string, unknown>>("/api/admin/backups/compare", ids),
  });
}

export interface BillingContractSummaryEntry {
  contract_id: number;
  client_name: string | null;
  plan_name: string | null;
  periods_closed: number;
  avg_cpe_count: number;
  faixa: number;
  total_amount: string;
}

export interface BillingSummaryResponse {
  total_engines: number;
  total_cpes: number;
  by_tier: Record<string, unknown>;
  by_status: Record<string, number>;
  contract?: {
    by_contract: BillingContractSummaryEntry[];
    total_contracts: number;
    total_amount: string;
  };
  revenue_estimate?: string;
}

export function useAdminBillingSummary() {
  return useQuery({
    queryKey: billingSummaryKey(),
    queryFn: () => apiClient.get<BillingSummaryResponse>("/api/admin/billing/summary"),
  });
}

export function useAdminBillingEngines(params?: Record<string, unknown>) {
  return useQuery({
    queryKey: billingEnginesKeys(params),
    queryFn: () => apiClient.get<{ data: unknown[]; total: number; page: number; limit: number }>("/api/admin/billing/engines", params as Record<string, string>),
  });
}

export function useAdminBillingOverage() {
  return useQuery({
    queryKey: billingOverageKey(),
    queryFn: () => apiClient.get<{ alerts: unknown[]; count: number }>("/api/admin/billing/overage"),
    refetchInterval: 60_000,
  });
}

export function useAdminBillingAntiFraud() {
  return useQuery({
    queryKey: billingAntiFraudKey(),
    queryFn: () => apiClient.get<{ alerts: unknown[]; count: number; note?: string }>("/api/admin/billing/anti-fraud"),
    refetchInterval: 300_000,
  });
}

const billingPeriodsKey = (params?: Record<string, unknown>) => [...billingAll, "periods", params] as const;

export interface BillingPeriodRow {
  id: number;
  contract_id: number | null;
  engine_node_id: number | null;
  metric_scope?: "engine" | "contract" | string | null;
  period_start: string;
  period_end: string;
  avg_cpe_count?: number;
  faixa?: number;
  amount: string | number;
  cpe_days?: string | number | null;
  avg_active?: string | number | null;
  due_date?: string | null;
  breakdown?: string | Record<string, unknown> | null;
  status: string;
  closed_at?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface BillingPeriodsResponse {
  data: BillingPeriodRow[];
  total: number;
  page: number;
  limit: number;
}

export function useAdminBillingPeriods(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: billingPeriodsKey(params),
    queryFn: () =>
      apiClient.get<BillingPeriodsResponse>(
        "/api/admin/billing/periods",
        params,
      ),
  });
}

/** Route cap: `periodsQuerySchema` in routes/admin/schemas.ts allows limit 1..100. */
const BILLING_PERIODS_PAGE_SIZE = 100;
/** Sane cap on the sequential walk — 10 pages × 100 rows = 1000 periods. */
const BILLING_PERIODS_MAX_PAGES = 10;

export interface AllBillingPeriodsResult {
  /** Every period row the walk could read (deduped by id). */
  rows: BillingPeriodRow[];
  /** Server-reported total for the whole scope (reporting only — never the loop condition). */
  total: number;
  /** True when the page cap (or an abort) cut the walk before a short page proved the end. */
  truncated: boolean;
}

/**
 * Reads the WHOLE `billing_periods` history, page by page.
 *
 * Why a client-side walk (Task 14 fix round MAJOR M3): GET
 * /api/admin/billing/periods accepts only `page`/`limit` — there is no
 * `contract_id` filter (periodsQuerySchema) — so a per-contract history cannot
 * be requested server-side. Filtering a single 100-row window left older
 * contracts with an empty/incomplete history. Walking the pages (limit 100,
 * capped at 10 pages) keeps the backend untouched and gives per-contract views
 * every row the API can serve.
 *
 * Follow-up worth doing: add an optional `contract_id` filter to the route +
 * `periodsQuerySchema`, at which point this hook collapses into one filtered
 * request (and the per-contract history stops depending on a global window).
 *
 * @param enabled false keeps the query idle (e.g. no detail drawer open).
 */
export function useAllBillingPeriods(enabled = true) {
  return useQuery({
    queryKey: billingPeriodsAllPagesKey(),
    enabled,
    // 5 min: the walk costs up to 10 sequential requests, so the old 30 s window
    // re-walked the whole history every time a detail drawer reopened.
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }): Promise<AllBillingPeriodsResult> => {
      const rows: BillingPeriodRow[] = [];
      const seen = new Set<number>();
      let total = 0;
      let page = 1;
      /** Set when a short/empty page PROVED the walk reached the end. */
      let reachedEnd = false;

      for (;;) {
        // Cancellation (the drawer closed) stops the walk; the rows already read
        // stay valid for the cache.
        if (signal.aborted) break;
        // Page cap (10 × 100 rows).
        if (page > BILLING_PERIODS_MAX_PAGES) break;

        const res = await apiClient.get<BillingPeriodsResponse>(
          "/api/admin/billing/periods",
          { page, limit: BILLING_PERIODS_PAGE_SIZE },
          signal,
        );
        total = Number(res.total ?? 0);
        const items = res.data ?? [];
        for (const row of items) {
          if (seen.has(row.id)) continue;
          seen.add(row.id);
          rows.push(row);
        }
        // Explicit stall test (round-2 N1b): an empty page or a SHORT page ends
        // the walk. The reported `total` is deliberately NOT the loop condition
        // — the API may omit it or report a value that drifts from the served
        // pages, which used to end the walk early (`rows.length >= total`).
        if (items.length <= 0 || items.length < BILLING_PERIODS_PAGE_SIZE) {
          reachedEnd = true;
          break;
        }
        page++;
      }

      // Honest truncation: true when the cap (or an abort) cut the walk before a
      // short page proved the end — regardless of what `total` claims.
      return { rows, total, truncated: !reachedEnd };
    },
  });
}

/**
 * Contract-scoped monthly close (plan 2026-10-21-core-contracts-billing
 * Task 9 — spec §2 R2#7; Task 13 UI wiring): POST /close takes {period:'YYYY-MM'}
 * and answers 200 {closed: <rows>}.
 * Accepts explicit 'YYYY-MM' from input selector or defaults to local-TZ format.
 * Throws when given an invalid explicit string format.
 */
export function useTriggerMonthlyClose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (periodInput?: string | Date) => {
      let period: string;
      if (typeof periodInput === "string") {
        if (!/^\d{4}-\d{2}$/.test(periodInput)) {
          throw new Error(`Invalid billing period format: "${periodInput}". Expected YYYY-MM.`);
        }
        period = periodInput;
      } else if (periodInput instanceof Date) {
        period = formatLocalPeriod(periodInput);
      } else if (periodInput === undefined || periodInput === null) {
        period = formatLocalPeriod(new Date());
      } else {
        throw new Error("Invalid billing period input.");
      }
      return apiClient.post<{ closed: number; message?: string }>("/api/admin/billing/close", { period });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: billingAll }),
  });
}

/**
 * Legacy engine-scoped close — POST /close/engine behind the CH-dependent
 * billingWorker: body {billing_period_end, confirm:true}, typed answers
 * 200 {status:'triggered'} / 503 billing_close_unavailable (no silent 202).
 */
export function useTriggerEngineClose() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (billingPeriodEnd: string) =>
      apiClient.post<{ status: string }>("/api/admin/billing/close/engine", {
        billing_period_end: billingPeriodEnd,
        confirm: true,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: billingAll }),
  });
}
